import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ModalLoading } from "../../../../../../common/components/modal_loading";


import PrimaryButton from "../../../../../../common/components/primary_button";
import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";

import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import styled from "@emotion/styled";
import {  useCallback, useEffect } from "react";
import { ContratoInformacoesForm, ContratoInformacoesSchema } from "../../../schemas/Contrato/contrato_informacoes";
import { DropDownFuncionario } from "../../../../../../common/components/dropdown_funcionario";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

type Props = {
  open: boolean;
  saving: boolean;
  selectedInfo: ContratoInformacoesForm | null;
  IdContrato: number;
  onSubmit: (form: ContratoInformacoesForm) => void;
  onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void,
  onClose: () => void;
};

export const InforModal = ({
  open,
  saving,
  IdContrato,
  selectedInfo,
  onOpenChangeHistory,
  onSubmit,
  onClose,
}: Props) => {
  const hookForm = useForm<ContratoInformacoesForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(ContratoInformacoesSchema),
  });

  useEffect(() => {
    if (open) {
      if (selectedInfo) {
        hookForm.reset({
          Data: selectedInfo.Data || undefined,
          DescInformacoes: selectedInfo.DescInformacoes,
          IdContrato: selectedInfo.IdContrato,
          IdContratoInformacoes: selectedInfo.IdContratoInformacoes,
          IdFuncionario: selectedInfo.IdFuncionario || undefined,
        });
      } else {
        hookForm.reset({
         IdContrato: IdContrato,
          IdContratoInformacoes: 0,
          Data: undefined,
          DescInformacoes: "",
          IdFuncionario: 0,
        });
      }
    }
  }, [hookForm, open, selectedInfo, IdContrato]);

  const handleChangeHistory = useCallback(() => {
    if (selectedInfo) {
      onOpenChangeHistory(selectedInfo.IdContratoInformacoes, LogTipoTabela.CONTRATO_INFORMACOES);
    }
  }, [selectedInfo, onOpenChangeHistory]);

  const handleSubmit = useCallback((form: ContratoInformacoesForm) => {
    if (!saving) {
      onSubmit(form);
    }
  }, [onSubmit, saving]);

  const handleClose = useCallback(() => {
    if (!saving) {
      onClose();
      hookForm.reset();
    }
  }, [hookForm, onClose, saving]);

  return (
    <Dialog
      open={open}
      fullWidth={true}
      maxWidth="md"
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(handleSubmit),
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "50rem",
          },
        },
      }}
    >
      <DialogTitle>
        Informações do Contrato
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <FormContainer title={selectedInfo ? "Editar" : "Cadastrar"} arrangement="fullRow">
            <>
             <FormDatePickerInput
                name='Data'
                label='Data'
                reactHookForm={hookForm}
                />
              <DropDownFuncionario
                name="IdFuncionario"
                label="Responsável "
                readOnly={saving}
                required={true}
                reactHookForm={hookForm}
                />
              <DefaultFormInput
                name='DescInformacoes'
                label="Informações"
                multiline={true}
                rows={6}
                required={true}
                reactHookForm={hookForm}
                />
            </>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div className="w-[220px]">
            {
              isNotNullOrUndefined(selectedInfo) &&
              <PrimaryButton
                buttonColor="yellow"
                onClick={handleChangeHistory}>
                <span className="text-xs">
                  Historico de alterações
                </span>
              </PrimaryButton>
            }
          </div>
          <div className="flex flex-row gap-2 w-full justify-end">
            <div 
              className="p-2 hover:bg-gray-100 cursor-pointer dark:hover:bg-gray-800 rounded-[4px] font-semibold"
              onClick={handleClose}
            >
              <h1 className="text-sm text-black dark:text-white">
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton type="submit" buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}